import { useEffect, useMemo, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { yupResolver } from '@hookform/resolvers/yup'

import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  Stack,
  Button,
  Tooltip,
} from '@mui/material'
import { PictureAsPdf as PdfIcon } from '@mui/icons-material'

import {
  CheckBoxGroupInput,
  CheckBoxInput,
  ControlledAutocompleteInput,
  SelectInput,
  TextInput,
} from '@components/inputs'
import { BodyPartEnum, CreateExamDto, Exam, Site } from '@services/api'
import { CreateExamFormValues } from '@services/dtos'
import { useSelector } from '@state/store'
import { useAppDispatch } from '@hooks/reduxHooks'
import { examDuration } from '@utils/constants'
import { procedureSchema } from '@utils/schemas'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { createExam, updateExam } from '@state/thunks/procedureThunk'

import { ExamAquisitionProtocol } from '@components/ExamAcquisitionProtocolManager'
import { CotationSelect } from './CotationSelect'
import { clearCcam } from '@state/reducers/procedureReducer'
import CompoundForm from '@components/forms/CompoundForm'
import { Delete as DeleteIcon } from '@mui/icons-material'
import ServerAutocomplete from '@components/autocomplete/ServerAutocomplete'
import { CompoundDto } from '@services/dtos'
import {
  getCompounds,
  setCompoundFilters,
} from '@state/reducers/compoundReducer'
import AddIcon from '@mui/icons-material/Add'
import { UpTransition } from '@components/animations'
import { PosologyGrid } from '@components/forms/posology/PosologyGrid'
import { getModality } from '@state/thunks/ForfaitThunk'

interface ProcedureFormProps {
  row?: Exam
  onClose?: () => void
}

export function ProcedureForm({ row, onClose }: ProcedureFormProps) {
  const dispatch = useAppDispatch()
  const intl = useIntl()

  // State
  const [selectedProperties, setSelectedProperties] = useState<string[]>([])
  const [selectedInstructions, setSelectedInstructions] = useState<string[]>([])
  const [protocolDialogOpen, setProtocolDialogOpen] = useState(false)
  const [ccamCodes, setCcamCodes] = useState<string[]>([])
  const [ngapCodes, setNgapCodes] = useState<string[]>([])
  const [compoundFormOpen, setCompoundFormOpen] = useState(false)

  // Selectors
  const instructions = useSelector(({ procedure }) => procedure.instructions)
  const user = useSelector(({ auth }) => auth.user)
  const sites = (user as unknown as { sites: Site[] })?.sites || []
  const modalities = useSelector(
    ({ forfaitsTechnique }) => forfaitsTechnique.modalities,
  )

  const modalityOptions = useMemo(
    () =>
      modalities.map((m) => ({
        label: m.libelle,
        value: m.id,
      })),
    [modalities],
  )

  const [selectedModaliteId, setSelectedModaliteId] = useState<number | null>(
    () => row?.modaliteId ?? null,
  )

  useEffect(() => {
    dispatch(getModality())
    return () => {
      dispatch(clearCcam())
    }
  }, [])

  // Form
  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    control,
  } = useForm<CreateExamFormValues>({
    resolver: yupResolver(procedureSchema),
    defaultValues: row
      ? {
          code: row.code,
          label: row.label,
          modality: row.modality,
          procedureTime: row.procedureTime,
          sex: row.sex,
          bodyPart: row.bodyPart,
          ageRange: row.ageRange,
          siteId: row.siteId,
          instructionsList: row.instructionsList.map((i) => i.label),
          isNuclear: row.isNuclear,
          dmpDelayInHours: row.publishToDmpDelays?.delayInHours,
          examCompounds: row.examCompounds?.map(
            ({ compound, posologySlices }) => ({
              compoundId: compound.id,
              posologySlices: posologySlices.map(
                (posologySlice) => posologySlice,
              ),
            }),
          ),
          medication: row.medication,
          wc: row.wc,
          instruction: row.instruction,
          cotation_ccam: row.cotation_ccam,
          cotation_ngap: row.cotation_ngap,
        }
      : undefined,
  })

  const siteId = watch('siteId')
  const isNuclear = watch('isNuclear')

  const {
    fields: examCompoundFields,
    append: appendExamCompound,
    remove: removeExamCompound,
  } = useFieldArray({
    control,
    name: 'examCompounds',
  })

  const fetchCompounds = async (search: string) => {
    dispatch(setCompoundFilters({ search }))
    const page = await dispatch(getCompounds()).unwrap()
    const examCompounds = watch('examCompounds') ?? []
    const compoundIds = examCompounds.map(
      (examCompound) => examCompound?.compoundId ?? '',
    )
    return (
      page?.datas?.filter((compound) => !compoundIds.includes(compound.id)) ??
      []
    )
  }

  // Options
  const bodyParts = Object.values(BodyPartEnum).map((part) => ({
    label: intl.formatMessage({ id: `enums.bodyPart.${part}` }),
    value: part,
  }))

  // Initialize from row
  useEffect(() => {
    if (!row) return

    const props: string[] = []
    if (row.contrast) props.push('contrast')
    if (row.dose) props.push('dose')
    if (row.fasted) props.push('fasted')
    if (row.shower) props.push('shower')
    if (row.medication) props.push('medication')
    if (row.wc) props.push('wc')

    setSelectedProperties(props)
    setSelectedInstructions(row.instructionsList?.map((i) => i.label) || [])
    setCcamCodes(row.cotation_ccam || [])
    setNgapCodes(row.cotation_ngap || [])
  }, [row])

  // Handlers
  const onSubmit = async (values: CreateExamFormValues) => {
    const payload = {
      ...values,
      dose: selectedProperties.includes('dose'),
      contrast: selectedProperties.includes('contrast'),
      wc: selectedProperties.includes('wc'),
      shower: selectedProperties.includes('shower'),
      fasted: selectedProperties.includes('fasted'),
      medication: selectedProperties.includes('medication'),
      isNuclear: isNuclear,
      instructionsList: selectedInstructions,
      cotation_ccam: ccamCodes,
      cotation_ngap: ngapCodes,
      modaliteId: selectedModaliteId,
      dmpDelayInHours:
        values.dmpDelayInHours != null
          ? Number(values.dmpDelayInHours)
          : undefined,
      examCompounds: values.examCompounds?.map(
        ({ compoundId, posologySlices }) => ({
          compoundId,
          posologySlices: posologySlices.map(
            ({
              weightMin,
              weightMax,
              type,
              patientType,
              activityMBq,
              eanmClass,
              eanmBaseActivityMBq,
              eanmMinActivityMBq,
            }) => ({
              weightMin,
              weightMax,
              type,
              patientType,
              activityMBq,
              eanmClass,
              eanmBaseActivityMBq,
              eanmMinActivityMBq,
            }),
          ),
        }),
      ),
    }

    if (row?.id) {
      const { examCompounds: _, ...rowWithoutCompounds } = row
      await dispatch(
        updateExam({
          id: row.id,
          body: { ...rowWithoutCompounds, ...payload },
        }),
      )
      dispatch(
        enqueueSnackbar({
          message: intl.formatMessage({ id: 'procedure-edit-success' }),
          type: 'success',
        }),
      )
    } else {
      await dispatch(createExam(payload as CreateExamDto))
      dispatch(
        enqueueSnackbar({
          message: intl.formatMessage({ id: 'procedure-add-success' }),
          type: 'success',
        }),
      )
    }
    onClose?.()
  }

  const renderInstruction = (instrLabel: string) => {
    const instr = instructions.find((i) => i.label === instrLabel)
    if (!instr?.imageUrl) return <></>
    return (
      <img
        src={`${process.env.REACT_APP_API_URL}/static${instr.imageUrl}`}
        width={24}
        height={24}
        alt={instrLabel}
      />
    )
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ p: 2 }}>
      {/* Row 1: Site & Code */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          fullWidth
          // size="small"
          label="Site"
          value={siteId || ''}
          onChange={(e) => setValue('siteId', Number(e.target.value))}
        >
          {sites.map((site) => (
            <MenuItem key={site.id} value={site.id}>
              {site.label}
            </MenuItem>
          ))}
        </TextField>
        <TextInput name="code" errors={errors} control={control} />
      </Stack>

      {/* Row 2: Label & Body Part */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextInput
          name="label"
          placeholder="label"
          errors={errors}
          control={control}
        />
        <ControlledAutocompleteInput
          label={intl.formatMessage({ id: 'bodyPart' })}
          input="bodyPart"
          options={bodyParts}
          control={control}
        />
      </Stack>

      {/* Row 3: Modality & Procedure Time */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <SelectInput
          name="modality"
          options={modalityOptions}
          errors={errors}
          onChange={(ev) => {
            const id = Number(ev.target.value)
            setSelectedModaliteId(id)
            const code = modalities.find((m) => m.id === id)?.code || ''
            setValue('modality', code)
          }}
        />
        <SelectInput
          name="procedureTime"
          options={examDuration}
          errors={errors}
          control={control}
        />
      </Stack>

      {/* Row 4: Sex & Age Range */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <SelectInput
          name="sex"
          options={[
            { value: 'F', label: 'Femme' },
            { value: 'M', label: 'Homme' },
            { value: 'A', label: 'Tous' },
          ]}
          errors={errors}
          control={control}
        />
        <SelectInput
          name="ageRange"
          options={[
            { value: 'baby', label: 'Bébé' },
            { value: 'kid', label: 'Enfant' },
            { value: 'adult', label: 'Adulte' },
            { value: 'senior', label: 'Senior' },
            { value: 'all', label: 'Tous' },
          ]}
          errors={errors}
          control={control}
        />
      </Stack>
      <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
        <TextInput
          name="dmpDelayInHours"
          type="number"
          errors={errors}
          control={control}
        />
      </Stack>
      <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
        <CheckBoxGroupInput
          onValueChange={setSelectedProperties}
          values={selectedProperties}
          options={[
            { value: 'dose', label: 'Dose' },
            { value: 'contrast', label: 'Produit de contraste' },
          ]}
        />
        <CheckBoxGroupInput
          onValueChange={setSelectedInstructions}
          values={selectedInstructions}
          renderText={renderInstruction}
          options={instructions.map((i) => ({
            value: i.label,
            label: i.label,
          }))}
        />
        <CheckBoxInput
          name="isNuclear"
          control={control}
          label="Nuclear Medicine"
        />
      </Stack>

      {/* Row 5: Cotation */}
      {isNuclear && (
        <Box sx={{ mb: 2 }}>
          <CotationSelect
            selectedCcamCodes={ccamCodes}
            selectedNgapCodes={ngapCodes}
            onCcamChange={setCcamCodes}
            onNgapChange={setNgapCodes}
            examDate={new Date()}
          />
        </Box>
      )}

      {/* Row: Compounds Select */}
      {isNuclear && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              appendExamCompound({
                posologySlices: [],
              })
            }}
          >
            Ajouter un MRP et sa posologie
          </Button>
          <Tooltip title="Créer un nouveau MRP" placement="top" arrow>
            <IconButton
              onClick={() => setCompoundFormOpen(true)}
              sx={{
                height: '36.5px',
                width: '40px',
                marginLeft: '6px',
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
              }}
              className="!rounded-lg"
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
          {examCompoundFields.map((_field, index) => (
            <fieldset
              className="border border-gray-200 p-2 rounded-lg mt-4"
              key={_field.id}
            >
              <legend className="text-xs text-gray-500 font-medium mb-2 border border-gray-200 pl-3 rounded-lg">
                Configuration MRP {index + 1}
                <IconButton
                  onClick={() => removeExamCompound(index)}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </legend>
              <ServerAutocomplete<CompoundDto>
                fetchService={fetchCompounds}
                onChange={(value: CompoundDto | null) => {
                  setValue(`examCompounds.${index}.compoundId`, value?.id ?? '')
                }}
                getValue={(value) => value?.id?.toString() || null}
                getOptionLabel={(value) => {
                  const compound = value as CompoundDto
                  return compound ? compound.name : ''
                }}
                debounceMs={300}
                placeholder="Rechercher un MRP"
                label="MRP"
                helperText={errors.examCompounds?.[index]?.compoundId?.message}
                error={!!errors.examCompounds?.[index]?.compoundId}
                minSearchLength={0}
                initialOptions={
                  row?.examCompounds?.[index]?.compound
                    ? [row.examCompounds[index].compound]
                    : []
                }
                value={row?.examCompounds?.[index]?.compound ?? null}
              />
              <PosologyGrid
                compoundIndex={index}
                control={control}
                errors={errors}
                watch={watch}
              />
            </fieldset>
          ))}
        </Box>
      )}

      {/* Row 7: Acquisition Protocol (edit only) */}
      {row && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={500}>
            Protocole:
          </Typography>
          {row.acquisitionProtocol ? (
            <>
              <IconButton
                size="small"
                onClick={() =>
                  window.open(
                    `${process.env.REACT_APP_API_URL}/${row.acquisitionProtocol?.pdfUrl}`,
                    '_blank',
                  )
                }
                disabled={!row.acquisitionProtocol?.pdfUrl}
                sx={{
                  color: row.acquisitionProtocol?.pdfUrl
                    ? 'error.main'
                    : 'grey.400',
                }}
              >
                <PdfIcon fontSize="small" />
              </IconButton>
              <Typography variant="body2" sx={{ flex: 1 }}>
                {row.acquisitionProtocol?.title || 'Aucun'}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Aucun
            </Typography>
          )}
          <Button
            onClick={() => setProtocolDialogOpen(true)}
            color="primary"
            size="small"
            variant="contained"
          >
            Modifier Protocol
          </Button>
        </Stack>
      )}

      {/* Actions */}
      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={2}
        sx={{ mt: 3 }}
      >
        <Button onClick={onClose} variant="outlined">
          Annuler
        </Button>
        <Button type="submit" color="secondary" variant="contained">
          Enregistrer
        </Button>
      </Stack>

      {/* Protocol Dialog */}
      {row && (
        <Dialog
          open={protocolDialogOpen}
          onClose={() => setProtocolDialogOpen(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogContent sx={{ height: 500 }}>
            <ExamAquisitionProtocol
              isEditMode={true}
              onEditModeChange={() => setProtocolDialogOpen(false)}
              onFileClick={(url) => window.open(url, '_blank')}
              exam={row}
            />
          </DialogContent>
        </Dialog>
      )}

      <Dialog
        fullWidth
        maxWidth="md"
        open={compoundFormOpen}
        TransitionComponent={UpTransition}
      >
        <CompoundForm onClose={() => setCompoundFormOpen(false)} />
      </Dialog>
    </Box>
  )
}
